import { ComponentProps, HTMLAttributes } from 'react'

import { AttributeSection, ColorSelector } from '@syconium/little-miss-figgy'

import { VariantSelectionFormTooltipContent } from '../../../app/_components/forms/VariantSelectionForm'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useTooltipFixture } from '../../../lib/hooks/useTooltipFixture'
import { formatPrettyColorName } from '../../../lib/utils'
import { ProductColor } from '../../../types/graphql'

import * as Styles from './styles'

export type ColorAttributeProps = {
  title: string
  selected?: ProductColor
  soldOut?: boolean
  label: string
  onSelect: ComponentProps<typeof ColorSelector>['onSelectColor']
  colors: ProductColor[]
  withTooltip?: boolean
  children?: React.ReactNode
} & Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'>

/**
 * An individual color attribute selector.
 */
export function ColorAttribute({
  title,
  selected,
  soldOut = false,
  label,
  onSelect,
  colors,
  withTooltip = false,
  children,
  ...rest
}: ColorAttributeProps) {
  const {
    detailsSection: { soldOutSectionTitleMessage },
  } = useTranslation()

  const tooltip = useTooltipFixture(label, 'colorCategory')

  return (
    <AttributeSection
      title={title}
      selection={
        <>
          {formatPrettyColorName(selected?.name ?? selected?.rawName ?? '')}
          {Boolean(soldOut && selected) && (
            <Styles.SoldOut>{soldOutSectionTitleMessage}</Styles.SoldOut>
          )}
        </>
      }
      tooltip={
        withTooltip &&
        tooltip && (
          <VariantSelectionFormTooltipContent
            title={tooltip.title}
            description={tooltip?.description}
          />
        )
      }
      tooltipLabel='Additional color information'
      {...rest}
    >
      <ColorSelector
        onSelectColor={onSelect}
        colors={colors}
        selectedColorKey={selected?.rawName}
        disableMobileHorizontalSwipe
      />
      {/* Optionally render additional markup within the attribute selector */}
      {children}
    </AttributeSection>
  )
}
